import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Users, Heart, Shield, ShieldAlert, MapPin, ArrowUpRight, Clock, ChevronRight } from "lucide-react";
import { SEO } from "@/components/SEO";
import BrutalistFooter from "@/components/custom/BrutalistFooter";
import { C, DISPLAY, BODY, MONO, Reveal, EditorialLabel, EBtn } from "@/styles/editorial";

const COUNTRIES = [
  {
    code: "ES", name: "España", flag: "ES",
    description: "Red de voluntarios y protectoras en toda España. Operativa desde 2024.",
    stats: { volunteers: "500+", shelters: "50+", emergencies: "10+" },
    color: C.red, status: "active",
  },
  {
    code: "EC", name: "Ecuador", flag: "EC",
    description: "Apoyo a refugios y santuarios animales en Ecuador.",
    stats: { volunteers: "En crecimiento", shelters: "En registro", emergencies: "3+" },
    color: "#ffd93d", status: "active",
  },
  {
    code: "VE", name: "Venezuela", flag: "VE",
    description: "Respuesta al terremoto de magnitud 7,5. Coordinación de emergencia en curso.",
    alert: "Terremoto magnitud 7,5 — San Felipe, Yaracuy",
    stats: { volunteers: "—", shelters: "—", emergencies: "EN CURSO" },
    color: "#a88efb", status: "coming_soon",
  },
  {
    code: "CO", name: "Colombia", flag: "CO",
    description: "En preparación. Contacto establecido con organizaciones locales.",
    stats: { volunteers: "—", shelters: "—", emergencies: "—" },
    color: C.blue, status: "coming_soon",
  },
];

const NAV_LINKS = [
  { l: "Hub", h: "/" },
  { l: "Conecta", h: "/conecta" },
  { l: "Recursos", h: "/recursos/ciudadania/guias" },
  { l: "Prepara", h: "/prepara" },
];

export default function CountrySelectPage() {
  const navigate = useNavigate();
  const [hovered, setHovered] = useState(null);

  const handleSelect = (country) => {
    if (country.status !== "active") return;
    localStorage.setItem("emergencia_country", country.code);
    localStorage.setItem("emergencia_country_name", country.name);
    navigate(`/onboarding?country=${country.code}`);
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ fontFamily: BODY, background: C.white }}>
      <SEO
        title="Selecciona tu país"
        description="Emergencia Animal opera en España, Ecuador, Venezuela y Colombia. Selecciona tu ubicación para acceder a la red de ayuda animal."
        path="/select-country"
      />

      {/* ── NAV ── */}
      <header className="sticky top-0 z-50 px-6 md:px-12 py-3"
        style={{ background: C.navy }}>
        <div className="max-w-[1400px] mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate("/")} className="flex items-center gap-2"
              style={{ background: "none", border: "none", cursor: "pointer" }}>
              <img src="/logos/logo-emergencia-animal.png" alt="EA" className="w-7 h-7 object-contain" />
              <span className="text-sm font-bold tracking-tight" style={{ fontFamily: DISPLAY, color: C.white }}>Emergencia Animal</span>
            </button>
            <div className="h-4 w-px hidden md:block" style={{ background: "rgba(255,255,255,0.15)" }} />
            <nav className="hidden md:flex items-center gap-1">
              {NAV_LINKS.map((lnk) => (
                <button key={lnk.l} onClick={() => navigate(lnk.h)}
                  className="px-3 py-1.5 text-[11px] font-semibold tracking-wider uppercase transition-all"
                  style={{ fontFamily: MONO, color: "rgba(255,255,255,0.55)", background: "none", border: "none", cursor: "pointer" }}>
                  {lnk.l}
                </button>
              ))}
            </nav>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-[10px] tracking-[0.15em] uppercase font-semibold hidden md:block" style={{ fontFamily: MONO, color: C.orange }}>
              Conecta Global
            </span>
          </div>
        </div>
      </header>

      {/* ── HERO ── */}
      <section className="px-6 md:px-12 pt-16 pb-8 md:pt-24 md:pb-12" style={{ background: C.navy }}>
        <div className="max-w-[900px] mx-auto text-center">
          <Reveal>
            <EditorialLabel text="Selecciona tu país" color={C.blue} />
            <h1 className="text-[clamp(2rem,5vw,3.5rem)] leading-[1.05] tracking-tight mb-4" style={{ fontFamily: DISPLAY, color: C.white }}>
              ¿Desde dónde{" "}
              <span style={{ color: C.blue }}>ayudas</span>?
            </h1>
            <p className="text-base max-w-lg mx-auto leading-relaxed" style={{ color: "rgba(255,255,255,0.55)" }}>
              Selecciona tu ubicación para acceder a emergencias, necesidades y oportunidades de ayuda cercanas.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ── COUNTRIES ── */}
      <section className="px-6 md:px-12 py-12 md:py-16 flex-1" style={{ background: "#f8f7f4" }}>
        <div className="max-w-[1000px] mx-auto grid md:grid-cols-2 gap-6">
          {COUNTRIES.map((country, idx) => {
            const isActive = country.status === "active";
            const isComingSoon = country.status === "coming_soon";
            return (
              <Reveal key={country.code} delay={idx * 0.08}>
                <button
                  onClick={() => handleSelect(country)}
                  onMouseEnter={() => setHovered(country.code)}
                  onMouseLeave={() => setHovered(null)}
                  disabled={!isActive}
                  className="relative text-left w-full transition-all duration-300"
                  style={{
                    background: C.white,
                    borderRadius: 4,
                    border: hovered === country.code && isActive ? `2px solid ${country.color}` : `1px solid ${C.navy}08`,
                    boxShadow: hovered === country.code && isActive ? "0 16px 40px rgba(0,0,0,0.08)" : "none",
                    transform: hovered === country.code && isActive ? "translateY(-4px)" : "none",
                    cursor: isActive ? "pointer" : "default",
                    opacity: isComingSoon ? 0.85 : 1,
                    overflow: "hidden",
                  }}
                  data-testid={`country-select-${country.code}`}
                >
                  {/* Alert banner for Venezuela */}
                  {country.alert && (
                    <div className="px-5 py-2 text-[10px] font-bold flex items-center gap-2 tracking-wider uppercase"
                      style={{ background: C.red, color: C.white, fontFamily: MONO }}>
                      <ShieldAlert className="w-3 h-3" strokeWidth={2} />
                      {country.alert}
                    </div>
                  )}

                  {/* Coming soon badge */}
                  {isComingSoon && (
                    <div className="absolute top-3 right-3 px-3 py-1 text-[9px] font-bold uppercase tracking-wider z-10"
                      style={{ background: `${country.color}15`, color: country.color, borderRadius: 3, fontFamily: MONO }}>
                      En breve
                    </div>
                  )}

                  <div className="p-6">
                    {/* Country header */}
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-12 h-12 flex items-center justify-center text-xl font-bold"
                        style={{ background: `${country.color}10`, borderRadius: 4, fontFamily: DISPLAY, color: country.color }}>
                        {country.code}
                      </div>
                      <div>
                        <h3 className="text-xl tracking-tight" style={{ fontFamily: DISPLAY, color: C.navy }}>{country.name}</h3>
                        <p className="text-xs mt-0.5 leading-relaxed" style={{ color: C.gray }}>{country.description}</p>
                      </div>
                    </div>

                    {/* Stats */}
                    <div className="flex gap-3 mb-5">
                      {[
                        { icon: Users, v: country.stats.volunteers, l: "Voluntarios", c: C.blue },
                        { icon: Heart, v: country.stats.shelters, l: "Refugios", c: C.pink },
                        { icon: Shield, v: country.stats.emergencies, l: "Emergencias", c: country.stats.emergencies === "EN CURSO" ? C.red : C.orange },
                      ].map((s, i) => (
                        <div key={i} className="flex-1 py-2.5 text-center" style={{ background: `${s.c}06`, borderRadius: 3 }}>
                          <s.icon className="w-3.5 h-3.5 mx-auto mb-1" style={{ color: s.c }} strokeWidth={1.8} />
                          <p className="text-[11px] font-bold" style={{ color: C.navy }}>{s.v}</p>
                          <p className="text-[8px] mt-0.5 tracking-wider" style={{ fontFamily: MONO, color: C.lightgray }}>{s.l}</p>
                        </div>
                      ))}
                    </div>

                    {/* CTA bar */}
                    <div className="flex items-center justify-between py-2.5 px-4"
                      style={{ background: isActive ? country.color : `${C.navy}06`, borderRadius: 3 }}>
                      <span className="text-xs font-bold tracking-wider" style={{ fontFamily: MONO, color: isActive ? C.white : C.lightgray }}>
                        {isActive ? "Entrar" : "En breve"}
                      </span>
                      {isActive ? (
                        <ArrowRight className="w-4 h-4" style={{ color: C.white }} />
                      ) : (
                        <Clock className="w-3.5 h-3.5" style={{ color: C.lightgray }} />
                      )}
                    </div>
                  </div>
                </button>
              </Reveal>
            );
          })}
        </div>
      </section>

      <BrutalistFooter />
    </div>
  );
}
